import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Chip, FormControl, Grid2, InputLabel, MenuItem, Modal, Select, TextField,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimeField } from "@mui/x-date-pickers/TimeField";
import { DateField } from "@mui/x-date-pickers/DateField";
import axios from "axios";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../Auth/AuthContext";
import { Link, useNavigate } from "react-router-dom";

const API = "https://api.sigmasystems.com.br/api/ag-barber";

const style = {
  position: "absolute",
  top:  { xs: "50%", md: "50%" },
  left: { xs: "50%", md: "50%" },
  transform: { xs: "translate(-50%, -50%)", md: "translate(-50%, -50%)" },
  width:     { xs: "95vw", md: 815 },
  height:    { xs: "auto", md: "auto" },
  maxHeight: { xs: "740", md: "90vh" },
  overflowY: "auto",
  boxSizing: "border-box",
  bgcolor: "background.paper",
  border: { xs: "none", md: "2px solid #4A7011" },
  borderRadius: { xs: 0, md: "8px" },
  boxShadow: 24,
  p: { xs: 2, md: 4 },
};

const btnPrimario = {
  color: "#fff", height: 40, width: 110, borderRadius: "5px", fontSize: "15px", fontWeight: "bold",
  backgroundColor: "#4a7011", textTransform: "none", "&:hover": { backgroundColor: "#293D09" },
};
const btnSecundario = {
  color: "#4a7011", height: 40, width: 110, border: "solid 2px #4a7011", borderRadius: "5px",
  fontSize: "15px", fontWeight: "bold", backgroundColor: "transparent", textTransform: "none",
  "&:hover": { backgroundColor: "#eeeeee" },
};

const DIAS_SEMANA = ["DOM", "SEG", "TER", "QUA", "QUI", "SEX", "SÁB"];
const DIAS_EXTENSO = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
const MESES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const FORM_VAZIO = {
  nome: "", descricao: "", telefone: "",
  servico_id: "", profissional_id: "",
  dia: null, hora: null,
};

/* ---------- helpers ---------- */

// Chave "YYYY-MM-DD" no horário LOCAL.
// NÃO use toISOString() aqui: ele converte para UTC e, no Brasil (UTC-3),
// depois das 21h o dia vira o dia seguinte.
function toKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// Gera as células do mês, incluindo dias do mês anterior/seguinte
// para completar as semanas (grade sempre começa no domingo).
function buildMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const start = new Date(year, month, 1 - first.getDay());
  const lastDay = new Date(year, month + 1, 0);
  const totalCells = Math.ceil((first.getDay() + lastDay.getDate()) / 7) * 7;

  return Array.from({ length: totalCells }, (_, i) => {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    return { date, key: toKey(date), currentMonth: date.getMonth() === month };
  });
}

const mensagemErro = (error, padrao) => error.response?.data?.message || padrao;
const hhmm = (t) => String(t).slice(0, 5);
// "09:00:00" (vindo do banco) -> Dayjs que o TimeField entende
const horaParaDayjs = (t) => dayjs(`2000-01-01T${t.length === 5 ? `${t}:00` : t}`);

// Quem está logado (o servidor é quem realmente decide as permissões; aqui é só a interface)
function lerSessao() {
  try {
    return jwtDecode(sessionStorage.getItem("token"));
  } catch {
    return null;
  }
}

function validarAg(f) {
  if (!f.nome.trim()) return "Informe o nome do cliente.";
  if (f.nome.trim().length > 145) return "O nome pode ter no máximo 145 caracteres.";
  if (f.telefone.trim().length > 35) return "O telefone pode ter no máximo 35 caracteres.";
  if (f.descricao.trim().length > 255) return "A descrição pode ter no máximo 255 caracteres.";
  if (!f.profissional_id) return "Selecione o profissional.";
  if (!f.servico_id) return "Selecione o serviço.";
  if (!f.dia || !f.dia.isValid()) return "Informe o dia.";
  if (!f.hora || !f.hora.isValid()) return "Informe o horário.";
  return null;
}

/* ---------- componente ---------- */

function Agendamentos() {
  const { nome, logout, ehAdmin } = useAuth();
  const navigate = useNavigate();

  const sessao = useMemo(lerSessao, []);
  const meuId = sessao?.id ?? null;

  const hoje = new Date();
  const [mesAtual, setMesAtual] = useState(new Date(hoje.getFullYear(), hoje.getMonth(), 1));
  const [diaSelecionado, setDiaSelecionado] = useState(hoje);
  const [busca, setBusca] = useState("");
  const [fechados, setFechados] = useState({});   // { [idDoProfissional]: true } => recolhido

  // dados vindos da API
  const [profissionais, setProfissionais] = useState([]);   // [{ id, name, role, services: [ids] }]
  const [services, setServices] = useState([]);
  const [contagens, setContagens] = useState({});           // { "2026-10-01": 3 }
  const [agDia, setAgDia] = useState([]);                   // agendamentos do dia selecionado
  const [carregandoDia, setCarregandoDia] = useState(false);
  const [versao, setVersao] = useState(0);                  // aumenta para recarregar calendário e dia

  // modal de adicionar / editar
  const [open, setOpen] = useState(false);
  const [editando, setEditando] = useState(null);           // agendamento em edição (null = novo)
  const [formAddAg, setFormAddAg] = useState(FORM_VAZIO);
  const [erroAg, setErroAg] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [livres, setLivres] = useState({ carregando: false, fechado: false, horarios: [] });

  const ano = mesAtual.getFullYear();
  const mes = mesAtual.getMonth();
  const hojeKey = toKey(hoje);
  const selecionadoKey = toKey(diaSelecionado);

  const grade = useMemo(() => buildMonthGrid(ano, mes), [ano, mes]);
  const de = grade[0].key;
  const ate = grade[grade.length - 1].key;

  /* ----- buscar dados ----- */

  // profissionais (com os serviços de cada um) e serviços
  useEffect(() => {
    (async () => {
      try {
        const [u, s] = await Promise.all([
          axios.post(`${API}/get-users`, {}),
          axios.post(`${API}/get-services`, {}),
        ]);
        setProfissionais(u.data);
        setServices(s.data);
      } catch (error) {
        console.error("Erro ao buscar profissionais e serviços:", error);
      }
    })();
  }, []);

  // quantidade de horários marcados em cada dia do calendário
  useEffect(() => {
    let cancelado = false;
    axios
      .get(`${API}/agenda-month`, { params: { from: de, to: ate } })
      .then(({ data }) => { if (!cancelado) setContagens(data.dias); })
      .catch((error) => console.error("Erro ao buscar o mês:", error));
    return () => { cancelado = true; };
  }, [de, ate, versao]);

  // agendamentos do dia selecionado
  useEffect(() => {
    let cancelado = false;
    setCarregandoDia(true);
    axios
      .get(`${API}/agenda-day`, { params: { date: selecionadoKey } })
      .then(({ data }) => { if (!cancelado) setAgDia(data); })
      .catch((error) => console.error("Erro ao buscar o dia:", error))
      .finally(() => { if (!cancelado) setCarregandoDia(false); });
    return () => { cancelado = true; };
  }, [selecionadoKey, versao]);

  // horários livres para o profissional, serviço e dia escolhidos no modal
  const diaForm = formAddAg.dia?.isValid() ? formAddAg.dia.format("YYYY-MM-DD") : null;
  useEffect(() => {
    if (!open) return undefined;
    const { profissional_id, servico_id } = formAddAg;
    if (!profissional_id || !servico_id || !diaForm) {
      setLivres({ carregando: false, fechado: false, horarios: [] });
      return undefined;
    }

    let cancelado = false;
    setLivres((l) => ({ ...l, carregando: true }));
    axios
      .get(`${API}/availability`, {
        params: { user_id: profissional_id, service_id: servico_id, date: diaForm, ignore_id: editando?.id },
      })
      .then(({ data }) => {
        if (!cancelado) setLivres({ carregando: false, fechado: data.fechado, horarios: data.livres });
      })
      .catch(() => {
        if (!cancelado) setLivres({ carregando: false, fechado: false, horarios: [] });
      });
    return () => { cancelado = true; };
  }, [open, formAddAg.profissional_id, formAddAg.servico_id, diaForm, editando]);

  /* ----- calendário e lista do dia ----- */

  // filtra pela busca (o telefone só existe para ADMIN, então só entra na busca dele)
  const doDia = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return agDia;
    return agDia.filter((a) =>
      `${a.name} ${a.service_name ?? ""} ${a.obs ?? ""} ${a.phone ?? ""} ${a.professional_name ?? ""}`
        .toLowerCase()
        .includes(termo));
  }, [agDia, busca]);

  // um grupo por profissional ativo; agendamentos de profissional desativado ganham grupo próprio
  const grupos = useMemo(() => {
    const mapa = new Map(profissionais.map((p) => [p.id, { id: p.id, nome: p.name, itens: [] }]));
    for (const a of doDia) {
      if (!mapa.has(a.user_id)) {
        mapa.set(a.user_id, {
          id: a.user_id,
          nome: `${a.professional_name ?? "Profissional"} (inativo)`,
          itens: [],
        });
      }
      mapa.get(a.user_id).itens.push(a);
    }
    return [...mapa.values()];
  }, [profissionais, doDia]);

  /* navegação */
  const mudarMes = (delta) => setMesAtual(new Date(ano, mes + delta, 1));
  const irParaHoje = () => {
    setMesAtual(new Date(hoje.getFullYear(), hoje.getMonth(), 1));
    setDiaSelecionado(hoje);
  };
  const selecionarDia = (cel) => {
    setDiaSelecionado(cel.date);
    // clicou num dia cinza (mês vizinho)? navega para aquele mês
    if (!cel.currentMonth) setMesAtual(new Date(cel.date.getFullYear(), cel.date.getMonth(), 1));
  };

  /* ----- modal: abrir / fechar ----- */

  const handleChangeAddAg = (field, value) =>
    setFormAddAg((prev) => ({ ...prev, [field]: value }));

  // ao trocar o profissional, o serviço só continua se ele também o realizar
  const handleChangeProfissional = (id) =>
    setFormAddAg((prev) => {
      const prof = profissionais.find((p) => p.id === id);
      const servicoOk = prof?.services?.includes(prev.servico_id);
      return { ...prev, profissional_id: id, servico_id: servicoOk ? prev.servico_id : "" };
    });

  const handleOpen = () => {
    setEditando(null);
    setErroAg("");
    setFormAddAg({
      ...FORM_VAZIO,
      dia: dayjs(diaSelecionado),
      profissional_id: ehAdmin ? "" : meuId ?? "",   // EMPLOYEE só agenda para si
    });
    setOpen(true);
  };

  const abrirEdicao = (a) => {
    if (!a.can_edit) return;
    setEditando(a);
    setErroAg("");
    setFormAddAg({
      nome: a.name,
      descricao: a.obs ?? "",
      telefone: a.phone ?? "",   // só vem preenchido para ADMIN
      servico_id: a.service_id,
      profissional_id: a.user_id,
      dia: dayjs(a.sched_date),
      hora: horaParaDayjs(a.start_time),
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditando(null);
    setErroAg("");
  };

  /* ----- modal: salvar e desmarcar ----- */

  const salvarAg = async () => {
    const erro = validarAg(formAddAg);
    if (erro) {
      setErroAg(erro);
      return;
    }

    setSalvando(true);
    setErroAg("");
    const dados = {
      user_id: formAddAg.profissional_id,
      service_id: formAddAg.servico_id,
      date: formAddAg.dia.format("YYYY-MM-DD"),
      start_time: formAddAg.hora.format("HH:mm:ss"),
      name: formAddAg.nome.trim(),
      obs: formAddAg.descricao.trim(),
    };
    // EMPLOYEE informa o telefone ao criar, mas não o vê nem o altera depois
    if (ehAdmin || !editando) dados.phone = formAddAg.telefone.trim();

    try {
      if (editando) {
        await axios.post(`${API}/update-schedule`, { id: editando.id, formEditSchedule: dados });
      } else {
        await axios.post(`${API}/register-schedule`, { formAddSchedule: dados });
      }
      // leva o calendário para o dia salvo, para o usuário ver o resultado
      const d = formAddAg.dia.toDate();
      setDiaSelecionado(d);
      setMesAtual(new Date(d.getFullYear(), d.getMonth(), 1));
      setVersao((v) => v + 1);
      handleClose();
    } catch (error) {
      setErroAg(mensagemErro(error, "Erro ao salvar agendamento."));
    } finally {
      setSalvando(false);
    }
  };

  const desmarcar = async () => {
    if (!editando || salvando) return;
    if (!window.confirm(
      `Desmarcar o horário de ${editando.name} às ${hhmm(editando.start_time)}?\n\nO horário ficará livre para novos agendamentos.`
    )) return;

    setSalvando(true);
    setErroAg("");
    try {
      await axios.post(`${API}/cancel-schedule`, { id: editando.id });
      setVersao((v) => v + 1);
      handleClose();
    } catch (error) {
      setErroAg(mensagemErro(error, "Não foi possível desmarcar o horário."));
    } finally {
      setSalvando(false);
    }
  };

  /* ----- opções dos selects do modal ----- */

  const opcoesProfissional = [...profissionais];
  if (editando && !profissionais.some((p) => p.id === editando.user_id)) {
    opcoesProfissional.push({ id: editando.user_id, name: `${editando.professional_name} (inativo)`, services: [] });
  }

  const profSel = profissionais.find((p) => p.id === formAddAg.profissional_id);
  const opcoesServico = services.filter((s) => profSel?.services?.includes(s.id));
  if (editando && formAddAg.servico_id && !opcoesServico.some((s) => s.id === formAddAg.servico_id)) {
    opcoesServico.push({ id: editando.service_id, name: editando.service_name ?? "Serviço indisponível" });
  }

  const horaSelecionada = formAddAg.hora?.isValid() ? formAddAg.hora.format("HH:mm") : null;
  const mostraTelefone = ehAdmin || !editando;   // EMPLOYEE não vê o telefone de um agendamento existente

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
      <div className="container-ag">
        <div className="content-ag">
          <div className="sideMenu">
            <div className="sideMenu-content">
              <div className="sideMenu-orientation">
                <div className="sideMenu-top">
                  <img src="/Images/bot-3d.png" className="sideMenu-bot3d" alt="" />
                  <h3 className="sideMenu-name">{nome}</h3>
                </div>
                <div className="sideMenu-btns">
                  <Link className="sideMenu-option-active" to="/Agendamentos">
                    <img src="/Images/calendar.svg" className="sideMenu-btn-icon" alt="" />
                    <span className="sideMenu-title">Agendamentos</span>
                  </Link>
                  {ehAdmin && (
                    <>
                    <Link className="sideMenu-option" to="/Configuracoes">
                      <img src="/Images/options.svg" className="sideMenu-btn-icon" alt="" />
                      <span className="sideMenu-title">Configuração</span>
                    </Link>
                    <Link className="sideMenu-option" to="/Lancamentos">
                      <img src="/Images/bag.svg" className="sideMenu-btn-icon" alt="" />
                      <span className="sideMenu-title">Lançamentos</span>
                    </Link>
                    </>
                  )}
                </div>
              </div>
              <div className="sideMenu-logout">
                <div className="sideMenu-logout-btn" onClick={() => { logout(); navigate("/"); }}>
                  <img src="/Images/logout.svg" className="sideMenu-logout-icon" alt="" />
                  <span className="sideMenu-title">Sair</span>
                </div>
                <img className="sideMenu-logo" src="/Images/sigmaBranco.png" alt="Sigma Systems" />
              </div>
            </div>
          </div>

          <div className="content">
            <div className="content-top">
              <h1 className="content-title">Agendamentos</h1>
              <Button onClick={handleOpen} sx={btnPrimario}>Adicionar</Button>
            </div>
            <div className="content-block">
              <div className="agenda">
                {/* ===== Calendário ===== */}
                <section className="agenda__calendario">
                  <header className="cal-topo">
                    <h2 style={{ width: "200px", fontSize: "24px" }}>{MESES[mes]} {ano}</h2>
                    <div className="cal-nav">
                      <button onClick={() => mudarMes(-1)} aria-label="Mês anterior">
                        <img src="/Images/arrow.svg" alt="" />
                      </button>
                      <button onClick={() => mudarMes(1)} aria-label="Próximo mês">
                        <img src="/Images/arrow.svg" style={{ rotate: "180deg" }} alt="" />
                      </button>
                    </div>
                    <button className="btn-hoje" onClick={irParaHoje}>Hoje</button>
                  </header>

                  <div className="cal-semana">
                    {DIAS_SEMANA.map((d) => <span key={d}>{d}</span>)}
                  </div>

                  <div className="cal-grade">
                    {grade.map((cel) => {
                      const qtd = contagens[cel.key] ?? 0;
                      const classes = [
                        "cal-dia",
                        !cel.currentMonth && "cal-dia--fora",
                        cel.key === hojeKey && "cal-dia--hoje",
                        cel.key === selecionadoKey && "cal-dia--selecionado",
                      ].filter(Boolean).join(" ");

                      return (
                        <button key={cel.key} className={classes} onClick={() => selecionarDia(cel)}>
                          <span className="cal-dia__numero">{cel.date.getDate()}</span>
                          {qtd > 0 && <span className="cal-dia__badge">{qtd}</span>}
                        </button>
                      );
                    })}
                  </div>
                </section>

                {/* ===== Agenda do dia ===== */}
                <aside className="agenda__dia">
                  <div className="dia-topo">
                    <div>
                      <small>{DIAS_EXTENSO[diaSelecionado.getDay()]}</small>
                      <h3>
                        {String(diaSelecionado.getDate()).padStart(2, "0")} de {MESES[diaSelecionado.getMonth()]}
                      </h3>
                    </div>
                    {selecionadoKey === hojeKey && <span className="tag-hoje">Hoje</span>}
                  </div>

                  <div className="dia-titulo">
                    <h3>Agenda do dia</h3>
                    <small>{doDia.length} {doDia.length === 1 ? "evento" : "eventos"}</small>
                  </div>

                  <input
                    className="dia-busca"
                    type="search"
                    placeholder="Pesquisar..."
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                  />

                  <div className="dia-lista">
                    {carregandoDia && <p className="prof__vazio">Carregando...</p>}

                    {!carregandoDia && grupos.map((g) => {
                      const aberto = !fechados[g.id];
                      return (
                        <div key={g.id} className="prof">
                          <button
                            className="prof__nome"
                            onClick={() => setFechados((f) => ({ ...f, [g.id]: aberto }))}
                            aria-expanded={aberto}
                          >
                            {g.nome}{" "}
                            <span className={`seta ${aberto ? "seta--aberta" : ""}`}>
                              <img src="/Images/arrow.svg" style={{ rotate: "90deg" }} alt="" />
                            </span>
                          </button>

                          {aberto && (
                            g.itens.length === 0 ? (
                              <p className="prof__vazio">Nenhum agendamento</p>
                            ) : (
                              <ul className="prof__itens">
                                {g.itens.map((a) => (
                                  <li
                                    key={a.id}
                                    className={a.can_edit ? "editavel" : undefined}
                                    onClick={a.can_edit ? () => abrirEdicao(a) : undefined}
                                    title={a.can_edit ? "Clique para editar" : undefined}
                                  >
                                    <span>
                                      <strong>{a.name}</strong>
                                      {a.phone ? ` - ${a.phone}` : ""} - {a.service_name ?? "Serviço"}
                                      {a.obs ? ` - ${a.obs}` : ""}
                                    </span>
                                    <time>{hhmm(a.start_time)}</time>
                                  </li>
                                ))}
                              </ul>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>
                </aside>
              </div>
            </div>
          </div>
        </div>

        {/* ===== Modal: adicionar / editar agendamento ===== */}
        <Modal open={open} onClose={handleClose}>
          <Box sx={style}>
            <div className="modal">
              <div className="modal-header">
                <div className="modal-header1">
                  <img className="modal-icon" src="/Images/calendar-green.svg" alt="" />
                  <span className="modal-title">
                    {editando ? "Editar Agendamento" : "Adicionar Agendamento"}
                  </span>
                </div>
                <div className="modal-header2">
                  {editando && (
                    <img
                      className="modal-trash-icon"
                      src="/Images/trash.svg"
                      alt="Desmarcar horário"
                      title="Desmarcar horário"
                      onClick={desmarcar}
                    />
                  )}
                </div>
              </div>

              <div className="modal-form">
                <Grid2 container spacing={1} justifyContent="space-between" sx={{ width: "100%" }}>
                  <Grid2 sx={{ width: { xs: "100%", md: 360 } }}>
                    <TextField label="Nome" fullWidth variant="outlined" color="success"
                      value={formAddAg.nome}
                      onChange={(e) => handleChangeAddAg("nome", e.target.value)} />
                  </Grid2>
                  <Grid2 sx={{ width: { xs: "100%", md: 360 } }}>
                    <TextField label="Descrição" fullWidth variant="outlined" color="success"
                      value={formAddAg.descricao}
                      onChange={(e) => handleChangeAddAg("descricao", e.target.value)} />
                  </Grid2>

                  {mostraTelefone && (
                    <Grid2 sx={{ width: { xs: "100%", md: 360 } }}>
                      <TextField label="Telefone" fullWidth variant="outlined" color="success"
                        value={formAddAg.telefone}
                        onChange={(e) => handleChangeAddAg("telefone", e.target.value)} />
                    </Grid2>
                  )}
                  <Grid2 sx={{ width: { xs: "100%", md: 360 } }}>
                    <FormControl fullWidth color="success">
                      <InputLabel>Profissional</InputLabel>
                      <Select label="Profissional" color="success"
                        MenuProps={{ style: { maxHeight: 360 } }}
                        disabled={!ehAdmin}   // EMPLOYEE só mexe nos próprios horários
                        value={formAddAg.profissional_id}
                        onChange={(e) => handleChangeProfissional(e.target.value)}>
                        {opcoesProfissional.map((p) => (
                          <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid2>

                  <Grid2 sx={{ width: { xs: "100%", md: 360 } }}>
                    <FormControl fullWidth color="success" disabled={!formAddAg.profissional_id}>
                      <InputLabel>Serviço</InputLabel>
                      <Select label="Serviço" color="success"
                        MenuProps={{ style: { maxHeight: 360 } }}
                        value={formAddAg.servico_id}
                        onChange={(e) => handleChangeAddAg("servico_id", e.target.value)}>
                        {opcoesServico.length === 0 && (
                          <MenuItem disabled value="">Este profissional não tem serviços</MenuItem>
                        )}
                        {opcoesServico.map((s) => (
                          <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid2>
                  <Grid2 sx={{ width: { xs: "100%", md: 170 } }}>
                    <DateField label="Dia" format="DD-MM-YYYY" color="success" fullWidth
                      value={formAddAg.dia}
                      onChange={(v) => handleChangeAddAg("dia", v)} />
                  </Grid2>
                  <Grid2 sx={{ width: { xs: "100%", md: 170 } }}>
                    <TimeField label="Hora" format="HH:mm" ampm={false} color="success" fullWidth
                      value={formAddAg.hora}
                      onChange={(v) => handleChangeAddAg("hora", v)} />
                  </Grid2>

                  {/* ----- horários livres: clique para preencher a hora ----- */}
                  {formAddAg.profissional_id && formAddAg.servico_id && diaForm && (
                    <Grid2 sx={{ width: "100%" }}>
                      <div style={{ fontSize: 13, color: "#666", marginBottom: 4 }}>Horários livres</div>
                      {livres.carregando ? (
                        <div style={{ fontSize: 13, color: "#999" }}>Buscando horários...</div>
                      ) : livres.fechado ? (
                        <div style={{ fontSize: 13, color: "#999" }}>O profissional não atende neste dia.</div>
                      ) : livres.horarios.length === 0 ? (
                        <div style={{ fontSize: 13, color: "#999" }}>Sem horários livres para este serviço neste dia.</div>
                      ) : (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 96, overflowY: "auto" }}>
                          {livres.horarios.map((t) => (
                            <Chip key={t} size="small" clickable color="success"
                              label={hhmm(t)}
                              variant={hhmm(t) === horaSelecionada ? "filled" : "outlined"}
                              onClick={() => handleChangeAddAg("hora", horaParaDayjs(t))} />
                          ))}
                        </div>
                      )}
                    </Grid2>
                  )}
                </Grid2>

                {erroAg && <Alert severity="error" sx={{ mt: 2 }}>{erroAg}</Alert>}

                <div className="modal-buttons">
                  <Button onClick={salvarAg} disabled={salvando} sx={btnPrimario}>
                    {editando ? "Salvar" : "Adicionar"}
                  </Button>
                  <Button onClick={handleClose} sx={btnSecundario}>Cancelar</Button>
                </div>
              </div>
            </div>
          </Box>
        </Modal>
      </div>
    </LocalizationProvider>
  );
}

export default Agendamentos;
